import axios from 'axios';
import {showForbiddenNotification} from '../redux/actions/actionsNotifications';
import {markSessionEndedNotice, resetSessionEndedNoticeForTests} from './sessionEndedNotice';

const SERVICE_CONTROLLERS: Record<string, string> = {
    AccountWeb: 'Identity',
    Users: 'IdentityManager',
    UsersManager: 'IdentityManager',
    Role: 'IdentityManager',
    Branch: 'IdentityManager',
    Language: 'IdentityManager',
    Status: 'IdentityManager',
    IdentityDictionary: 'Identity',
    Notification: 'Identity',
    Campaign: 'Campaigns',
    CampaignContacts: 'Campaigns',
    CampaignsDictionary: 'Campaigns',
    DialerDictionary: 'Campaigns',
    UserResource: 'UserDataProvider',
    CallDetailInfo: 'UserDataProvider',
    CallBanUsersManager: 'UserDataProvider',
    Statistic: 'Statistic',
    Monitoring: 'MonitoringManager',
    QueueMonitor: 'MonitoringManager',
    Player: 'MonitoringManager',
    Audio: 'AudioManager',
    QualityControl: 'QualityControl',
    QualityControlStatistic: 'QualityControl',
    VoipUsers: 'VoipPBXManager',
    VoipDictionary: 'VoipPBXManager',
    Trunks: 'VoipPBXManager',
    Queues: 'VoipPBXManager',
    SpyCall: 'VoipPBXManager',
    SkillGroup: 'VoipPBXManager',
    LogAlerts: 'VoipPBXManager',
    IVRConfig: 'IVRManager'
};

const ANONYMOUS_AUTH_PATHS = [
    'accountweb/login',
    'accountweb/googlelogin',
    'accountweb/google-login',
    'accountweb/google-register',
    'accountweb/sendrecoverypassword',
    'accountweb/validaterecoverypassword',
    'accountweb/verify-2fa'
];

type LogoutHandler = (reason: string, details?: Record<string, unknown>) => void;
type ForbiddenDispatch = (action: unknown) => void;

let installed = false;
let requestInterceptorId: number | null = null;
let interceptorId: number | null = null;
let logoutHandler: LogoutHandler | null = null;
let forbiddenDispatch: ForbiddenDispatch | null = null;

export function resolveApiService(url: string | undefined | null): string | null {
    const controller = controllerName(url);
    if (!controller) {
        return null;
    }
    return SERVICE_CONTROLLERS[controller] || null;
}

export function registerUnauthorizedLogout(handler: LogoutHandler) {
    logoutHandler = handler;
    return () => {
        if (logoutHandler === handler) {
            logoutHandler = null;
        }
    };
}

export function bindHttpStatusAuthStore(store: {dispatch: ForbiddenDispatch}) {
    forbiddenDispatch = (action) => store.dispatch(action);
}

export function installHttpStatusAuth() {
    if (installed) {
        return;
    }
    requestInterceptorId = axios.interceptors.request.use((config) => {
        (config as RequestConfig).authTokenAtSend = readSessionToken();
        return config;
    });
    interceptorId = axios.interceptors.response.use(
        (response) => response,
        (error) => {
            handleHttpStatus(error);
            return Promise.reject(error);
        }
    );
    installed = true;
}

export function resetHttpStatusAuthForTests() {
    logoutHandler = null;
    forbiddenDispatch = null;
    resetSessionEndedNoticeForTests();
    if (requestInterceptorId != null) {
        axios.interceptors.request.eject(requestInterceptorId);
        requestInterceptorId = null;
    }
    if (interceptorId != null) {
        axios.interceptors.response.eject(interceptorId);
        interceptorId = null;
    }
    installed = false;
}

function handleHttpStatus(error: {response?: {status?: number; data?: unknown}; config?: RequestConfig}) {
    const status = error?.response?.status;
    const url = requestUrl(error?.config);
    const service = resolveApiService(url);
    if (!service || (status !== 401 && status !== 403)) {
        return;
    }

    if (status === 401) {
        if (isAnonymousAuthRequest(url) || isStaleSessionRequest(error?.config) || !hasSessionToken() || !logoutHandler) {
            if (!isAnonymousAuthRequest(url) && !hasSessionToken() && error?.config?.authTokenAtSend) {
                markSessionEndedNotice();
            }
            return;
        }
        logoutHandler('unauthorized', {status, url, service});
        markSessionEndedNotice();
        return;
    }

    forbiddenDispatch?.(showForbiddenNotification({message: readErrorMessage(error?.response?.data)}));
}

function controllerName(url: string | undefined | null): string | null {
    if (!url) {
        return null;
    }
    const path = url.split('?')[0];
    const parts = path.split('/').filter(Boolean);
    return parts.find((part) => SERVICE_CONTROLLERS[part]) || null;
}

type RequestConfig = {url?: string; baseURL?: string; authTokenAtSend?: string | null};

function requestUrl(config?: RequestConfig): string {
    const url = config?.url || '';
    const baseURL = config?.baseURL || '';
    if (!url) {
        return baseURL;
    }
    if (url.startsWith('http') || !baseURL) {
        return url;
    }
    return `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
}

function isAnonymousAuthRequest(url: string): boolean {
    const path = url.toLowerCase();
    return ANONYMOUS_AUTH_PATHS.some((part) => path.includes(part));
}

function readSessionToken(): string | null {
    try {
        return sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
    } catch {
        return null;
    }
}

function hasSessionToken(): boolean {
    return !!readSessionToken();
}

function isStaleSessionRequest(config?: RequestConfig): boolean {
    const sentToken = config?.authTokenAtSend;
    const currentToken = readSessionToken();
    return !!sentToken && !!currentToken && sentToken !== currentToken;
}

function readErrorMessage(data: unknown): string | null {
    if (typeof data === 'string') {
        const text = data.trim();
        if (!text || text.startsWith('<')) {
            return null;
        }
        return text.slice(0, 300);
    }
    if (!data || typeof data !== 'object') {
        return null;
    }
    const payload = data as Record<string, unknown>;
    for (const key of ['message', 'title', 'detail']) {
        const value = payload[key];
        if (typeof value !== 'string') {
            continue;
        }
        const text = value.trim();
        if (!text || text === 'Access Denied' || /object reference not set|nullreferenceexception|exception of type/i.test(text)) {
            continue;
        }
        return text.slice(0, 300);
    }
    return null;
}
